import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, BedDouble, BedSingle, User, Check, X, Edit, Home, Building2 } from "lucide-react";
import api from "../lib/api";

export default function RoomsPage() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [roomDetailOpen, setRoomDetailOpen] = useState(false);
  const [guestDetailOpen, setGuestDetailOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [editingRoom, setEditingRoom] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [formData, setFormData] = useState({
    room_number: "",
    room_type: "single",
    floor: 1,
    price_per_night: 35,
    price_per_month: 350,
    description: "",
  });
  const [roomEditData, setRoomEditData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [roomsRes, bedsRes, guestsRes] = await Promise.all([
        api.get("/rooms"),
        api.get("/beds"),
        api.get("/guests"),
      ]);
      
      const bedsMap = {};
      bedsRes.data.forEach((bed) => {
        bedsMap[bed.id] = bed;
      });
      
      const enrichedRooms = roomsRes.data.map((room) => ({
        ...room,
        beds: room.beds?.map((bed) => ({
          ...bed,
          ...bedsMap[bed.id],
        })) || [],
      }));
      
      setRooms(enrichedRooms);
      setGuests(guestsRes.data);
    } catch (error) {
      console.error("Error fetching rooms:", error);
      toast.error("Zimmer konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/rooms", {
        ...formData,
        floor: parseInt(formData.floor),
        price_per_night: parseFloat(formData.price_per_night),
        price_per_month: parseFloat(formData.price_per_month),
      });
      
      const roomId = response.data.id;
      const bedCount = formData.room_type === "double" ? 2 : formData.room_type === "wohnung" ? 0 : 1;
      
      for (let i = 1; i <= bedCount; i++) {
        await api.post("/beds", {
          room_id: roomId,
          bed_number: `${formData.room_number}-${i}`,
          is_available: true,
        });
      }
      
      toast.success(formData.room_type === "wohnung" ? "Wohnung erstellt" : "Zimmer erstellt");
      setDialogOpen(false);
      setFormData({
        room_number: "",
        room_type: "single",
        floor: 1,
        price_per_night: 35,
        price_per_month: 350,
        description: "",
      });
      fetchData();
    } catch (error) {
      console.error("Error creating room:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const openRoomDetail = (room) => {
    setSelectedRoom(room);
    setRoomEditData({
      room_number: room.room_number,
      room_type: room.room_type,
      floor: room.floor,
      price_per_night: room.price_per_night,
      price_per_month: room.price_per_month,
      description: room.description || "",
    });
    setEditingRoom(false);
    setRoomDetailOpen(true);
  };

  const handleRoomUpdate = async () => {
    try {
      await api.put(`/rooms/${selectedRoom.id}`, {
        ...roomEditData,
        floor: parseInt(roomEditData.floor),
        price_per_night: parseFloat(roomEditData.price_per_night),
        price_per_month: parseFloat(roomEditData.price_per_month),
      });
      toast.success("Zimmer aktualisiert");
      setEditingRoom(false);
      setRoomDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const openGuestDetail = (guestId) => {
    const guest = guests.find((g) => g.id === guestId);
    if (guest) {
      setSelectedGuest(guest);
      setGuestDetailOpen(true);
    }
  };

  const filteredRooms = rooms.filter((room) => {
    if (selectedType === "all") return true;
    return room.room_type === selectedType;
  });

  // Kata göre grupla
  const roomsByFloor = filteredRooms.reduce((acc, room) => {
    const floor = room.floor || 0;
    if (!acc[floor]) {
      acc[floor] = [];
    }
    acc[floor].push(room);
    return acc;
  }, {});

  // Katları sırala
  const sortedFloors = Object.keys(roomsByFloor).sort((a, b) => parseInt(a) - parseInt(b));

  // Oda durumu sıralaması için yardımcı fonksiyon
  const getRoomStatusOrder = (room) => {
    const availableBeds = room.beds?.filter((b) => b.is_available).length || 0;
    const totalBeds = room.beds?.length || 0;
    const isWohnung = room.room_type === "wohnung";
    
    if (isWohnung) return 3; // Wohnungen en sonda
    if (availableBeds === 0 && totalBeds > 0) return 0; // Belegt önce
    if (availableBeds < totalBeds) return 1; // Teilweise ortada
    return 2; // Verfügbar en son (normal odalar için)
  };

  // Her kattaki odaları duruma göre sırala
  Object.keys(roomsByFloor).forEach(floor => {
    roomsByFloor[floor].sort((a, b) => {
      const statusA = getRoomStatusOrder(a);
      const statusB = getRoomStatusOrder(b);
      if (statusA !== statusB) return statusA - statusB;
      // Aynı durumdaysa oda numarasına göre sırala
      return (a.room_number || '').localeCompare(b.room_number || '', 'de', { numeric: true });
    });
  });

  const getFloorLabel = (floor) => {
    const f = parseInt(floor);
    if (f === 0) return "Erdgeschoss";
    if (f === 1) return "1. Etage";
    if (f === 2) return "2. Etage";
    if (f === 3) return "3. Etage";
    return `${f}. Etage`;
  };

  const stats = {
    total: rooms.length,
    single: rooms.filter((r) => r.room_type === "single").length,
    double: rooms.filter((r) => r.room_type === "double").length,
    wohnung: rooms.filter((r) => r.room_type === "wohnung").length,
    totalBeds: rooms.reduce((acc, r) => acc + (r.beds?.length || 0), 0),
    availableBeds: rooms.reduce((acc, r) => acc + (r.beds?.filter((b) => b.is_available).length || 0), 0),
  };

  const getRoomTypeLabel = (type) => {
    switch (type) {
      case "single": return "Einzelzimmer";
      case "double": return "Doppelzimmer";
      case "wohnung": return "Wohnung";
      default: return type;
    }
  };

  const getRoomIcon = (type) => {
    switch (type) {
      case "wohnung": return <Home className="h-4 w-4 sm:h-5 sm:w-5 text-violet-500" strokeWidth={1.5} />;
      case "double": return <BedDouble className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" strokeWidth={1.5} />;
      default: return <BedSingle className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" strokeWidth={1.5} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="rooms-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Zimmer
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Zimmer, Wohnungen und Betten verwalten
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-room-btn">
              <Plus className="h-4 w-4 mr-2" />
              Neu erstellen
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Neues Zimmer/Wohnung</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="room_number">Nummer *</Label>
                  <Input
                    id="room_number"
                    name="room_number"
                    placeholder="z.B. 301"
                    value={formData.room_number}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Typ *</Label>
                  <Select value={formData.room_type} onValueChange={(value) => setFormData((prev) => ({ ...prev, room_type: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Einzelzimmer</SelectItem>
                      <SelectItem value="double">Doppelzimmer</SelectItem>
                      <SelectItem value="wohnung">Wohnung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="floor">Etage</Label>
                  <Input id="floor" name="floor" type="number" value={formData.floor} onChange={handleInputChange} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_per_night">€/Nacht</Label>
                  <Input id="price_per_night" name="price_per_night" type="number" step="0.01" value={formData.price_per_night} onChange={handleInputChange} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_per_month">€/Monat</Label>
                  <Input id="price_per_month" name="price_per_month" type="number" step="0.01" value={formData.price_per_month} onChange={handleInputChange} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Input id="description" name="description" value={formData.description} onChange={handleInputChange} />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Abbrechen</Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">Erstellen</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats - Scrollable on mobile */}
      <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 md:grid-cols-6">
        {[
          { label: "Gesamt", value: stats.total },
          { label: "Einzel", value: stats.single },
          { label: "Doppel", value: stats.double },
          { label: "Wohnung", value: stats.wohnung, color: "text-violet-600" },
          { label: "Betten", value: stats.totalBeds },
          { label: "Frei", value: stats.availableBeds, color: "text-emerald-600" },
        ].map((stat, i) => (
          <Card key={i} className="border-slate-200 flex-shrink-0 w-24 sm:w-auto">
            <CardContent className="p-3 sm:pt-6">
              <div className={`text-lg sm:text-2xl font-bold ${stat.color || 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stat.value}
              </div>
              <p className="text-[10px] sm:text-sm text-slate-600">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter Tabs */}
      <Tabs value={selectedType} onValueChange={setSelectedType}>
        <TabsList className="w-full sm:w-auto grid grid-cols-4 sm:flex">
          <TabsTrigger value="all" className="text-xs sm:text-sm">Alle</TabsTrigger>
          <TabsTrigger value="single" className="text-xs sm:text-sm">Einzel</TabsTrigger>
          <TabsTrigger value="double" className="text-xs sm:text-sm">Doppel</TabsTrigger>
          <TabsTrigger value="wohnung" className="text-xs sm:text-sm">Wohnung</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Rooms Grid - Grouped by Floor */}
      <div className="space-y-6">
        {sortedFloors.map((floor) => {
          const floorRooms = roomsByFloor[floor];
          const floorBeds = floorRooms.reduce((acc, r) => acc + (r.beds?.length || 0), 0);
          const floorAvailable = floorRooms.reduce((acc, r) => acc + (r.beds?.filter((b) => b.is_available).length || 0), 0);
          
          return (
            <div key={floor} className="space-y-3">
              {/* Floor Header - Sticky */}
              <div className="flex items-center justify-between bg-slate-100 rounded-lg px-4 py-2 sticky top-16 z-20 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-slate-600" />
                  <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {getFloorLabel(floor)}
                  </h2>
                  <Badge variant="outline" className="text-xs">
                    {floorRooms.length} {floorRooms.length === 1 ? 'Zimmer' : 'Zimmer'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-600">Betten:</span>
                  <span className={`font-medium ${floorAvailable === 0 ? 'text-slate-500' : 'text-emerald-600'}`}>
                    {floorAvailable}/{floorBeds} frei
                  </span>
                </div>
              </div>
              
              {/* Floor Rooms Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {floorRooms.map((room) => {
          const availableBeds = room.beds?.filter((b) => b.is_available).length || 0;
          const totalBeds = room.beds?.length || 0;
          const isFullyOccupied = availableBeds === 0 && totalBeds > 0;
          const isWohnung = room.room_type === "wohnung";
          
          // Hover rengi duruma göre
          let hoverClass = "hover:border-emerald-300 hover:shadow-emerald-100";
          if (isFullyOccupied) {
            hoverClass = "hover:border-slate-300 hover:shadow-slate-100";
          } else if (availableBeds < totalBeds) {
            hoverClass = "hover:border-amber-300 hover:shadow-amber-100";
          }
          if (isWohnung) {
            hoverClass = "hover:border-violet-300 hover:shadow-violet-100";
          }

          return (
            <Card
              key={room.id}
              className={`border-slate-200 transition-all cursor-pointer ${hoverClass} ${
                isFullyOccupied ? "bg-slate-50" : ""
              } ${isWohnung ? "border-violet-200" : ""}`}
              onClick={() => openRoomDetail(room)}
              data-testid={`room-card-${room.room_number}`}
            >
              <CardHeader className="pb-2 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {isWohnung ? "W" : "Zi."} {room.room_number}
                  </CardTitle>
                  {getRoomIcon(room.room_type)}
                </div>
                <div className="flex gap-1 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] sm:text-xs ${isWohnung ? "border-violet-300 text-violet-700" : ""}`}>
                    {getRoomTypeLabel(room.room_type)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0 space-y-3">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-600">Monat</span>
                  <span className="font-mono font-medium">€{room.price_per_month?.toFixed(0)}</span>
                </div>

                {!isWohnung && totalBeds > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-slate-600">Betten</span>
                      <span className={`font-medium ${isFullyOccupied ? "text-slate-500" : "text-emerald-600"}`}>
                        {availableBeds}/{totalBeds}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {room.beds?.map((bed) => (
                        <div
                          key={bed.id}
                          className={`p-2 rounded-md border text-xs ${
                            bed.is_available ? "bg-emerald-50 border-emerald-200" : "bg-slate-100 border-slate-200"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!bed.is_available && bed.current_guest_id) {
                              openGuestDetail(bed.current_guest_id);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {bed.is_available ? (
                              <Check className="h-3 w-3 text-emerald-600" />
                            ) : (
                              <User className="h-3 w-3 text-slate-600" />
                            )}
                            <span className="font-medium">Bett {bed.bed_number}</span>
                          </div>
                          {!bed.is_available && bed.current_guest_name && (
                            <div className="mt-1 pl-5 text-blue-600 font-medium truncate">
                              {bed.current_guest_name}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isWohnung && room.description && (
                  <p className="text-xs text-slate-600 line-clamp-2">{room.description}</p>
                )}

                <div className="pt-2 border-t border-slate-100">
                  {isWohnung ? (
                    <Badge className="w-full justify-center bg-violet-100 text-violet-700 border-violet-200 text-xs">Wohnung</Badge>
                  ) : isFullyOccupied ? (
                    <Badge className="w-full justify-center status-occupied text-xs">Belegt</Badge>
                  ) : availableBeds < totalBeds ? (
                    <Badge className="w-full justify-center status-pending text-xs">Teilweise</Badge>
                  ) : (
                    <Badge className="w-full justify-center status-available text-xs">Verfügbar</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
              </div>
            </div>
          );
        })}
      </div>

      {sortedFloors.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <BedDouble className="h-12 w-12 mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
            <p className="text-slate-500">Keine Zimmer gefunden</p>
          </CardContent>
        </Card>
      )}

      {/* Room Detail Dialog */}
      <Dialog open={roomDetailOpen} onOpenChange={setRoomDetailOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span>{selectedRoom?.room_type === "wohnung" ? "Wohnung" : "Zimmer"} {selectedRoom?.room_number}</span>
              <Button variant="ghost" size="sm" onClick={() => setEditingRoom(!editingRoom)}>
                <Edit className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedRoom && (
            <div className="space-y-4">
              {editingRoom ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Nummer</Label>
                      <Input value={roomEditData.room_number} onChange={(e) => setRoomEditData((prev) => ({ ...prev, room_number: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Etage</Label>
                      <Input type="number" value={roomEditData.floor} onChange={(e) => setRoomEditData((prev) => ({ ...prev, floor: e.target.value }))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>€/Nacht</Label>
                      <Input type="number" step="0.01" value={roomEditData.price_per_night} onChange={(e) => setRoomEditData((prev) => ({ ...prev, price_per_night: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>€/Monat</Label>
                      <Input type="number" step="0.01" value={roomEditData.price_per_month} onChange={(e) => setRoomEditData((prev) => ({ ...prev, price_per_month: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setEditingRoom(false)} className="flex-1">Abbrechen</Button>
                    <Button onClick={handleRoomUpdate} className="flex-1 bg-slate-900 hover:bg-slate-800">Speichern</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Typ</p>
                      <p className="font-medium text-sm">{getRoomTypeLabel(selectedRoom.room_type)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Etage</p>
                      <p className="font-medium text-sm">{selectedRoom.floor}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">€/Nacht</p>
                      <p className="font-medium font-mono text-sm">€{selectedRoom.price_per_night?.toFixed(2)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">€/Monat</p>
                      <p className="font-medium font-mono text-sm">€{selectedRoom.price_per_month?.toFixed(2)}</p>
                    </div>
                  </div>
                  {selectedRoom.beds?.length > 0 && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-2">Betten ({selectedRoom.beds.length})</p>
                      <div className="space-y-1">
                        {selectedRoom.beds.map((bed) => (
                          <div key={bed.id} className="flex items-center justify-between text-sm">
                            <span>Bett {bed.bed_number}</span>
                            <span className={bed.is_available ? "text-emerald-600" : "text-slate-600"}>
                              {bed.is_available ? "Frei" : bed.current_guest_name || "Belegt"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Guest Detail Dialog - Enhanced with Edit Functionality */}
      <Dialog open={guestDetailOpen} onOpenChange={setGuestDetailOpen}>
        <DialogContent className="max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <User className="h-5 w-5" />
              Bewohner Details
            </DialogTitle>
          </DialogHeader>
          {selectedGuest && (
            <div className="space-y-4">
              {/* Personal Info Section */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Persönliche Daten
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Vorname</p>
                    <p className="font-medium text-sm">{selectedGuest.first_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Nachname</p>
                    <p className="font-medium text-sm">{selectedGuest.last_name}</p>
                  </div>
                </div>
                {selectedGuest.phone && (
                  <div>
                    <p className="text-xs text-slate-500">Telefon</p>
                    <p className="font-medium text-sm">{selectedGuest.phone}</p>
                  </div>
                )}
                {selectedGuest.email && (
                  <div>
                    <p className="text-xs text-slate-500">E-Mail</p>
                    <p className="font-medium text-sm">{selectedGuest.email}</p>
                  </div>
                )}
                {selectedGuest.address && (
                  <div>
                    <p className="text-xs text-slate-500">Adresse</p>
                    <p className="font-medium text-sm">
                      {selectedGuest.address}
                      {selectedGuest.postal_code && `, ${selectedGuest.postal_code}`}
                      {selectedGuest.city && ` ${selectedGuest.city}`}
                    </p>
                  </div>
                )}
              </div>

              {/* Jobcenter Section - Only for Sozial */}
              {selectedGuest.bewohner_type !== "privat" && (selectedGuest.jobcenter_name || selectedGuest.aktenzeichen) && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 space-y-3">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Jobcenter Information
                  </h4>
                  {selectedGuest.jobcenter_name && (
                    <div>
                      <p className="text-xs text-blue-600">Jobcenter</p>
                      <p className="font-medium text-sm text-blue-900">{selectedGuest.jobcenter_name}</p>
                    </div>
                  )}
                  {selectedGuest.aktenzeichen && (
                    <div>
                      <p className="text-xs text-blue-600">Aktenzeichen</p>
                      <p className="font-medium font-mono text-sm text-blue-900">{selectedGuest.aktenzeichen}</p>
                    </div>
                  )}
                  {selectedGuest.sachbearbeiter_name && (
                    <div>
                      <p className="text-xs text-blue-600">Sachbearbeiter</p>
                      <p className="font-medium text-sm text-blue-900">
                        {selectedGuest.sachbearbeiter_name}
                        {selectedGuest.sachbearbeiter_phone && ` - ${selectedGuest.sachbearbeiter_phone}`}
                      </p>
                    </div>
                  )}
                  {selectedGuest.leistung_end_date && (
                    <div className="pt-2 border-t border-blue-200">
                      <p className="text-xs text-blue-600">Leistung bis</p>
                      <p className="font-medium text-sm text-blue-900">{selectedGuest.leistung_end_date}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Privat Section - Only for Self-Paying */}
              {selectedGuest.bewohner_type === "privat" && (selectedGuest.arbeitgeber || selectedGuest.einkommen) && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100 space-y-3">
                  <h4 className="font-medium text-emerald-800 flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Arbeit & Einkommen
                  </h4>
                  {selectedGuest.arbeitgeber && (
                    <div>
                      <p className="text-xs text-emerald-600">Arbeitgeber</p>
                      <p className="font-medium text-sm text-emerald-900">{selectedGuest.arbeitgeber}</p>
                    </div>
                  )}
                  {selectedGuest.einkommen && (
                    <div>
                      <p className="text-xs text-emerald-600">Monatliches Einkommen</p>
                      <p className="font-medium text-sm text-emerald-900">€{parseFloat(selectedGuest.einkommen).toFixed(2)}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Guarantor Section */}
              {selectedGuest.guarantor_name && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-100 space-y-2">
                  <h4 className="font-medium text-amber-800">Bürge</h4>
                  <div>
                    <p className="text-xs text-amber-600">Name</p>
                    <p className="font-medium text-sm text-amber-900">{selectedGuest.guarantor_name}</p>
                  </div>
                  {selectedGuest.guarantor_phone && (
                    <div>
                      <p className="text-xs text-amber-600">Telefon</p>
                      <p className="font-medium text-sm text-amber-900">{selectedGuest.guarantor_phone}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes Section */}
              {selectedGuest.notes && (
                <div className="p-4 bg-slate-100 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-2">Notizen</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedGuest.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-slate-200">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setGuestDetailOpen(false)}
                >
                  Schließen
                </Button>
                <Button 
                  className="flex-1 bg-slate-900 hover:bg-slate-800" 
                  onClick={() => { 
                    setGuestDetailOpen(false); 
                    navigate('/guests'); 
                  }}
                  data-testid="edit-guest-from-room-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Zur Bearbeitung
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
